// <s-announcement-rotator> — shows one [data-announcement] at a time, sliding the next one up into place.
// Static (first message only) for prefers-reduced-motion; pauses when the tab is hidden, on hover/focus,
// and via the [data-announcement-toggle] button. One component per file.
class SAnnouncementRotator extends HTMLElement {
  #ac = null;
  #timer = null;
  #index = 0;
  #paused = false;
  #hovered = false;
  #mql = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#sync(), { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });
    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, { signal });
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#hovered = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", () => { this.#hovered = false; this.#sync(); }, { signal });
    this.querySelector("[data-announcement-toggle]")?.addEventListener(
      "click",
      () => { this.#paused = !this.#paused; this.#sync(); },
      { signal },
    );
    this.#show(0);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  get #items() {
    return [...this.querySelectorAll("[data-announcement]")];
  }

  #show(next) {
    const items = this.#items;
    if (!items.length) return;
    const previous = this.#index;
    this.#index = (next + items.length) % items.length;
    items.forEach((item, i) => {
      item.toggleAttribute("data-active", i === this.#index);
      item.toggleAttribute("data-leaving", i === previous && i !== this.#index);
      item.toggleAttribute("inert", i !== this.#index);
      item.setAttribute("aria-hidden", i === this.#index ? "false" : "true");
    });
  }

  #sync() {
    clearInterval(this.#timer);
    this.#timer = null;
    const reduced = this.#mql.matches;
    const toggle = this.querySelector("[data-announcement-toggle]");
    this.toggleAttribute("data-paused", this.#paused);
    if (toggle) {
      toggle.hidden = reduced || this.#items.length < 2;
      toggle.setAttribute("aria-pressed", this.#paused ? "true" : "false");
      toggle.setAttribute("aria-label", this.#paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    }
    if (reduced || this.#paused || this.#hovered || document.hidden || this.#items.length < 2) return;
    const interval = parseInt(this.dataset.interval, 10) || 5000;
    this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
  }
}

if (!customElements.get("s-announcement-rotator")) {
  customElements.define("s-announcement-rotator", SAnnouncementRotator);
}
